import React, { FC, memo, useEffect, useMemo, useRef } from "react";
import { ClickAwayListener, Paper, Popper } from "@mui/material";
import { BiddingTable } from "@/components/containers/BiddingTable";

interface QuickBidPopoverProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    auctionId: string;
    lotId: string;
    isTender?: boolean;
    onBidPlaced?: (amount: number) => void;
}

// Room the popover needs below the button before it opens above it instead.
const NEEDED_BELOW = 440;

// Quick bid from a lot card: a popover beside the button with the mobile quick bid layout. Closes on a click outside
// it, like the documents and info popovers.
const QuickBidPopover: FC<QuickBidPopoverProps> = ({ anchorEl, onClose, auctionId, lotId, isTender = false, onBidPlaced }) => {
    // Position is fixed when it opens so it never jumps afterwards: the side (below / above) is chosen once from the room
    // available, and it is anchored to where the button was (moving with the page scroll) rather than to the button
    // itself, so the card changing (e.g. showing the new highest bid) does not nudge it.
    const { virtualAnchor, placement } = useMemo(() => {
        if (!anchorEl) return { virtualAnchor: null, placement: "bottom-start" as const };
        const rect = anchorEl.getBoundingClientRect();
        const pageTop = rect.top + window.scrollY;
        const pageLeft = rect.left + window.scrollX;
        const spaceBelow = window.innerHeight - rect.bottom;
        const side = spaceBelow >= NEEDED_BELOW || spaceBelow >= rect.top ? "bottom-start" as const : "top-start" as const;
        return {
            placement: side,
            virtualAnchor: {
                getBoundingClientRect: () => {
                    const top = pageTop - window.scrollY;
                    const left = pageLeft - window.scrollX;
                    return { top, left, bottom: top + rect.height, right: left + rect.width, width: rect.width, height: rect.height, x: left, y: top, toJSON: () => ({}) } as DOMRect;
                },
            },
        };
    }, [anchorEl]);

    // A pinned anchor is not watched for scrolling, so follow the page (and any scrolling container) ourselves.
    const popperRef = useRef<any>(null);
    useEffect(() => {
        if (!anchorEl) return;
        const follow = () => popperRef.current?.update();
        window.addEventListener("scroll", follow, { capture: true, passive: true });
        window.addEventListener("resize", follow);
        return () => {
            window.removeEventListener("scroll", follow, { capture: true } as any);
            window.removeEventListener("resize", follow);
        };
    }, [anchorEl]);

    return (
        <Popper
            popperRef={popperRef}
            open={Boolean(anchorEl)}
            anchorEl={virtualAnchor}
            placement={placement}
            style={{ zIndex: 1050 }}
            popperOptions={{ strategy: "fixed" }}
            modifiers={[
                { name: "flip", enabled: false },
                { name: "preventOverflow", options: { boundary: "viewport", padding: 12, altAxis: false } },
                { name: "offset", options: { offset: [0, 8] } },
            ]}
        >
            {/* Delayed like the other popovers, so a click on the quick bid button itself closes it rather than reopening it.
                Clicks in dialogs opened from inside (React portals) count as inside. */}
            <ClickAwayListener onClickAway={() => setTimeout(onClose, 5)}>
                <Paper className="pb-popover pb-quickbid" onClick={(event) => event.stopPropagation()}>
                    {/* Opens on the lot card, so no title or lot name. */}
                    <BiddingTable auctionId={auctionId} lotId={lotId} showCompactView={true} onBidPlaced={onBidPlaced} />
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

export default memo(QuickBidPopover);
